/**
 * editor Commands
 * High-level editing commands that work with the AST undo/redo system
 */

import {
  createDeleteBlockOperation,
  createDeleteTextOperation,
  createIndentListItemOperation,
  createInsertBlockOperation,
  createInsertTextOperation,
  createMergeBlocksOperation,
  createRemoveMarkOperation,
  createSetBlockAttrsOperation,
  createSetMarkOperation,
  createSplitBlockOperation,
  createUnindentListItemOperation,
  createUnwrapEncryptedOperation,
  createWrapEncryptedOperation,
} from './astOperations'
import { AstUndoRedoManager, createSelection } from './astUndoRedoManager'
import { detectParagraphDirection } from '../../components/editor/editor.utils'
import { logger } from '../../utils/logger'

import {
  AstSelection,
  Block,
  ContentAST,
  InlineContent,
  Mark,
  createDivider,
  createHeading,
  createList,
  createParagraph,
  createQuote,
  findBlockById,
  generateBlockId,
  generateEncryptionId,
  getBlockText,
} from './index'

/**
 * editor command interface
 */
export interface EditorCommand {
  execute(manager: AstUndoRedoManager): boolean
  canExecute(manager: AstUndoRedoManager): boolean
  description: string
}

/**
 * editor commands class with fluent API
 */
export class AstEditorCommands {
  constructor(private manager: AstUndoRedoManager) {}

  /**
   * Gets the current AST
   */
  public getAST(): ContentAST {
    return this.manager.getCurrentAST()
  }

  /**
   * Gets the current selection
   */
  public getSelection(): AstSelection {
    return this.manager.getCurrentSelection()
  }

  /**
   * Updates selection
   */
  public setSelection(selection: AstSelection): this {
    this.manager.updateSelection(selection)
    return this
  }

  /**
   * Inserts text at current selection
   */
  public insertText(text: string, marks?: Mark[]): this {
    const selection = this.manager.getCurrentSelection()
    if (this.isEmbedBlock(selection.blockId)) {
      return this
    }
    const operation = createInsertTextOperation(selection.blockId, selection.offset, text, marks)

    const newSelection = createSelection(selection.blockId, selection.offset + text.length)

    this.manager.executeOperation(operation, newSelection, `Insert "${text}"`)
    return this
  }

  /**
   * Deletes text in current selection
   */
  public deleteText(length?: number): this {
    const selection = this.manager.getCurrentSelection()
    if (this.isEmbedBlock(selection.blockId)) {
      return this
    }
    const deleteLength = length || selection.length || 1

    if (deleteLength <= 0) {
      return this
    }

    const operation = createDeleteTextOperation(selection.blockId, selection.offset, deleteLength)
    const newSelection = createSelection(selection.blockId, selection.offset)

    this.manager.executeOperation(operation, newSelection, `Delete ${deleteLength} characters`)
    return this
  }

  /**
   * Replaces selected text
   */
  public replaceText(text: string, marks?: Mark[]): this {
    const selection = this.manager.getCurrentSelection()

    if (selection.length && selection.length > 0) {
      this.deleteText(selection.length)
    }

    return this.insertText(text, marks)
  }

  /**
   * Applies formatting mark to selection
   */
  public applyMark(mark: Mark, length?: number): this {
    const selection = this.manager.getCurrentSelection()
    if (this.isEmbedBlock(selection.blockId)) {
      return this
    }
    const markLength = length || selection.length || 1

    if (markLength <= 0) {
      return this
    }

    const operation = createSetMarkOperation(selection.blockId, selection.offset, markLength, mark)
    const newSelection = createSelection(selection.blockId, selection.offset, markLength)

    this.manager.executeOperation(operation, newSelection, `Apply ${this.getMarkDescription(mark)}`)
    return this
  }

  /**
   * Removes formatting mark from selection
   */
  public removeMark(mark: Mark, length?: number): this {
    const selection = this.manager.getCurrentSelection()
    if (this.isEmbedBlock(selection.blockId)) {
      return this
    }
    const markLength = length || selection.length || 1

    if (markLength <= 0) {
      return this
    }

    const operation = createRemoveMarkOperation(selection.blockId, selection.offset, markLength, mark)
    const newSelection = createSelection(selection.blockId, selection.offset, markLength)

    this.manager.executeOperation(operation, newSelection, `Remove ${this.getMarkDescription(mark)}`)
    return this
  }

  /**
   * Toggles formatting mark on selection
   */
  public toggleMark(mark: Mark): this {
    // This would require checking if the mark is already applied
    // For now, just apply the mark
    return this.applyMark(mark)
  }

  /**
   * Inserts a new block after current block
   */
  public insertBlock(blockType: 'paragraph' | 'heading' | 'list' | 'quote' | 'divider', options?: any): this {
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()
    const currentBlockIndex = ast.blocks.findIndex((b) => b.id === selection.blockId)

    if (currentBlockIndex === -1) {
      return this
    }

    let newBlock: Block
    const newBlockId = generateBlockId()

    switch (blockType) {
      case 'paragraph':
        newBlock = createParagraph()
        break
      case 'heading':
        newBlock = createHeading(options?.level || 1)
        break
      case 'list':
        newBlock = createList(options?.ordered || false)
        break
      case 'quote':
        newBlock = createQuote()
        break
      case 'divider':
        newBlock = createDivider()
        break
      default:
        return this
    }

    newBlock.id = newBlockId

    const operation = createInsertBlockOperation(newBlockId, newBlock, currentBlockIndex + 1)
    const newSelection = createSelection(newBlockId, 0)

    this.manager.executeOperation(operation, newSelection, `Insert ${blockType}`)
    return this
  }

  /**
   * Splits current block at cursor position
   */
  public splitBlock(): this {
    const selection = this.manager.getCurrentSelection()
    if (this.isEmbedBlock(selection.blockId)) {
      return this
    }
    const newBlockId = generateBlockId()

    const operation = createSplitBlockOperation(selection.blockId, selection.offset, newBlockId)
    const newSelection = createSelection(newBlockId, 0)

    this.manager.executeOperation(operation, newSelection, 'Split block')
    return this
  }

  /**
   * Merges current block with next block
   */
  public mergeWithNextBlock(): this {
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()
    const currentBlockIndex = ast.blocks.findIndex((b) => b.id === selection.blockId)

    if (currentBlockIndex === -1 || currentBlockIndex >= ast.blocks.length - 1) {
      return this
    }

    const nextBlock = ast.blocks[currentBlockIndex + 1]
    const currentBlock = ast.blocks[currentBlockIndex]
    if (currentBlock.type === 'embed' || nextBlock.type === 'embed') {
      return this
    }
    const currentBlockText = getBlockText(currentBlock)

    const operation = createMergeBlocksOperation(nextBlock.id, selection.blockId)
    const newSelection = createSelection(selection.blockId, currentBlockText.length)

    this.manager.executeOperation(operation, newSelection, 'Merge blocks')
    return this
  }

  /**
   * Deletes current block
   */
  public deleteBlock(): this {
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()
    const currentBlockIndex = ast.blocks.findIndex((b) => b.id === selection.blockId)

    if (currentBlockIndex === -1 || ast.blocks.length <= 1) {
      return this
    }

    // Determine where to place cursor after deletion
    let newSelectionBlockId: string
    if (currentBlockIndex > 0) {
      // Move to previous block
      newSelectionBlockId = ast.blocks[currentBlockIndex - 1].id
    } else {
      // Move to next block (which will become first)
      newSelectionBlockId = ast.blocks[currentBlockIndex + 1].id
    }

    const operation = createDeleteBlockOperation(selection.blockId, currentBlockIndex)
    const newSelection = createSelection(newSelectionBlockId, 0)

    this.manager.executeOperation(operation, newSelection, 'Delete block')
    return this
  }

  /**
   * Converts multiple blocks to a single list
   */
  public convertBlocksToList(blockIds: string[], ordered: boolean = false): this {
    logger.debug('🔄 convertBlocksToList called with blockIds:', blockIds, 'ordered:', ordered)
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()

    if (blockIds.length === 0) {
      logger.warn('🔄 No blocks to convert to list')
      return this
    }

    // Find all blocks to convert
    const blocksToConvert = blockIds.map((id) => findBlockById(ast, id)).filter(Boolean)
    if (blocksToConvert.length === 0) {
      logger.warn('🔄 No valid blocks found to convert to list')
      return this
    }
    // A mixed selection cannot be collapsed into one list without deleting or
    // moving a source reference, so leave the whole selection intact.
    if (blocksToConvert.some((block) => block?.type === 'embed')) {
      return this
    }

    logger.debug(
      '🔄 Found blocks to convert:',
      blocksToConvert.map((b) => ({ id: b!.id, type: b!.type })),
    )

    // Create list items from the blocks
    const listItems = blocksToConvert
      .map((block) => {
        if (!block) {
          return null
        }

        // Handle different block types
        if ('content' in block) {
          // Paragraph, heading, quote blocks
          return {
            id: generateBlockId(),
            content: block.content,
            attrs: block.attrs,
          }
        } else if ('items' in block) {
          // List block - flatten its items
          return block.items.map((item) => ({
            id: generateBlockId(),
            content: item.content,
            attrs: item.attrs,
          }))
        }

        return null
      })
      .flat()
      .filter(Boolean)

    // Create the new list block
    const listBlock = createList(ordered)
    listBlock.id = blocksToConvert[0]!.id // Use the first block's ID
    listBlock.items = listItems as any[]

    // Create operations to delete all blocks and insert the list
    const operations = []

    // Sort blocks by their index in the AST (reverse order for deletion)
    const blockIndices = blocksToConvert
      .map((block) => ({
        block: block!,
        index: ast.blocks.findIndex((b) => b.id === block!.id),
      }))
      .sort((a, b) => b.index - a.index) // Reverse order for deletion

    // Delete all blocks (in reverse order)
    for (const { block, index } of blockIndices) {
      operations.push(createDeleteBlockOperation(block.id, index, block))
    }

    // Insert the list at the position of the first block
    const firstBlockIndex = Math.min(...blockIndices.map((bi) => bi.index))
    operations.push(createInsertBlockOperation(listBlock.id, listBlock, firstBlockIndex))

    logger.debug(
      '🔄 Executing transaction with operations:',
      operations.map((op) => ({ type: op.type, blockId: op.blockId })),
    )

    this.manager.executeTransaction(operations, selection, 'Convert blocks to list')

    logger.debug('🔄 Transaction executed for convertBlocksToList')
    return this
  }

  /**
   * Convert a list back to individual paragraph blocks
   * Returns the IDs of the created paragraph blocks
   */
  public convertListToParagraphs(listBlockId: string): string[] {
    logger.debug('🔄 convertListToParagraphs called with listBlockId:', listBlockId)
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()

    // Find the list block
    const listBlock = findBlockById(ast, listBlockId)
    if (!listBlock || listBlock.type !== 'list') {
      logger.warn('🔄 Block is not a list or not found:', listBlockId)
      return []
    }

    logger.debug('🔄 Found list block:', { id: listBlock.id, type: listBlock.type, itemCount: listBlock.items.length })

    // Create paragraph blocks from list items
    const paragraphBlocks = listBlock.items.map((item) => {
      const paragraph = createParagraph()
      paragraph.id = generateBlockId()
      paragraph.content = item.content
      paragraph.attrs = item.attrs
      return paragraph
    })

    logger.debug(
      '🔄 Created paragraph blocks:',
      paragraphBlocks.map((p) => ({ id: p.id, type: p.type })),
    )

    // Create operations
    const operations = []
    const listIndex = ast.blocks.findIndex((b) => b.id === listBlockId)

    // Delete the list block
    operations.push(createDeleteBlockOperation(listBlockId, listIndex, listBlock))

    // Insert paragraph blocks at the same position
    paragraphBlocks.forEach((paragraph, index) => {
      operations.push(createInsertBlockOperation(paragraph.id, paragraph, listIndex + index))
    })

    logger.debug(
      '🔄 Executing transaction with operations:',
      operations.map((op) => ({ type: op.type, blockId: op.blockId })),
    )

    this.manager.executeTransaction(operations, selection, 'Convert list to paragraphs')

    logger.debug('🔄 Transaction executed for convertListToParagraphs')

    // Return the IDs of the created paragraphs
    const createdIds = paragraphBlocks.map((p) => p.id)
    logger.debug('🔄 Created paragraph IDs:', createdIds)
    return createdIds
  }

  /**
   * Changes block type
   */
  public changeBlockType(
    newType: 'paragraph' | 'heading' | 'quote' | 'list',
    options?: { level?: number; ordered?: boolean },
  ): this {
    logger.debug('🔄 changeBlockType called with:', newType, options)
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()
    logger.debug(
      '🔄 Current AST:',
      JSON.stringify(
        ast.blocks.map((b) => ({ id: b.id, type: b.type })),
        null,
        2,
      ),
    )
    logger.debug('🔄 Current selection:', selection)
    const currentBlock = findBlockById(ast, selection.blockId)
    logger.debug(
      '🔄 Found current block:',
      currentBlock ? { id: currentBlock.id, type: currentBlock.type } : 'NOT FOUND',
    )

    if (!currentBlock) {
      logger.warn('🔄 No current block found, returning early')
      return this
    }
    if (currentBlock.type === 'embed') {
      return this
    }

    // Create new block with same content but different type
    let newBlock: Block
    switch (newType) {
      case 'paragraph':
        if (!('content' in currentBlock)) {
          return this
        }
        newBlock = { ...createParagraph(), content: currentBlock.content }
        break
      case 'heading': {
        if (!('content' in currentBlock)) {
          return this
        }
        const level = (options?.level || 1) as 1 | 2 | 3 | 4 | 5 | 6
        newBlock = {
          ...createHeading(level),
          content: currentBlock.content,
        }
        break
      }
      case 'quote':
        if (!('content' in currentBlock)) {
          return this
        }
        newBlock = { ...createQuote(), content: currentBlock.content }
        break
      case 'list': {
        // Convert content to list items
        const listBlock = createList(options?.ordered || false)
        if ('content' in currentBlock && currentBlock.content.length > 0) {
          // Convert existing content to first list item
          listBlock.items = [
            {
              id: generateBlockId(),
              content: currentBlock.content,
              attrs: currentBlock.attrs,
            },
          ]
        } else {
          // Create empty list item
          listBlock.items = [
            {
              id: generateBlockId(),
              content: [],
              attrs: currentBlock.attrs,
            },
          ]
        }
        newBlock = listBlock
        break
      }
      default:
        return this
    }

    newBlock.id = currentBlock.id

    // This requires a compound operation: delete + insert
    const currentBlockIndex = ast.blocks.findIndex((b) => b.id === selection.blockId)
    const operations = [
      createDeleteBlockOperation(selection.blockId, currentBlockIndex, currentBlock), // Include the deleted block for undo
      createInsertBlockOperation(selection.blockId, newBlock, currentBlockIndex),
    ]

    logger.debug(
      '🔄 Executing transaction with operations:',
      operations.map((op) => ({ type: op.type, blockId: op.blockId })),
    )
    logger.debug('🔄 New block created:', {
      id: newBlock.id,
      type: newBlock.type,
      content: (newBlock as any).content || (newBlock as any).items,
    })

    this.manager.executeTransaction(operations, selection, `Change to ${newType}`)

    logger.debug(
      '🔄 Transaction executed, new AST:',
      JSON.stringify(
        this.manager.getCurrentAST().blocks.map((b) => ({ id: b.id, type: b.type })),
        null,
        2,
      ),
    )
    return this
  }

  /**
   * Marks selected text as encrypted (wraps in EncryptedNode)
   */
  public markSelectionAsEncrypted(encryptionId?: string): this {
    const selection = this.manager.getCurrentSelection()

    if (!selection.length || selection.length <= 0) {
      logger.warn('No selection to encrypt')
      return this
    }

    const ast = this.manager.getCurrentAST()
    const block = findBlockById(ast, selection.blockId)

    if (!block || !('content' in block)) {
      logger.warn('Block does not support encryption')
      return this
    }

    // Generate encryption ID if not provided
    const generatedId = encryptionId || generateEncryptionId()

    // Extract the content that will be wrapped (for undo)
    const wrappedContent: InlineContent[] = []
    let currentOffset = 0

    for (const node of block.content) {
      if (node.type === 'text') {
        const nodeLength = node.text.length
        const nodeEnd = currentOffset + nodeLength

        if (nodeEnd > selection.offset && currentOffset < selection.offset + selection.length) {
          // This node intersects with selection
          const startInNode = Math.max(0, selection.offset - currentOffset)
          const endInNode = Math.min(nodeLength, selection.offset + selection.length - currentOffset)
          const selectedText = node.text.slice(startInNode, endInNode)

          if (selectedText) {
            wrappedContent.push({
              type: 'text',
              text: selectedText,
              marks: node.marks,
            })
          }
        }

        currentOffset += nodeLength
      }
    }

    // Create the wrap operation
    const operation = createWrapEncryptedOperation(
      selection.blockId,
      selection.offset,
      selection.length,
      generatedId,
      wrappedContent,
    )

    // Keep selection at the same position (now inside encrypted node)
    const newSelection = createSelection(selection.blockId, selection.offset + selection.length)

    this.manager.executeOperation(operation, newSelection, 'Mark for encryption')
    return this
  }

  /**
   * Removes encryption marking from an EncryptedNode at cursor
   */
  public unmarkEncryptedNode(encryptionId: string): this {
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()
    const block = findBlockById(ast, selection.blockId)

    if (!block || !('content' in block)) {
      logger.warn('Block does not support encryption')
      return this
    }

    // Find the encrypted node with the given ID
    let encryptedNodeOffset = 0
    let found = false

    for (const node of block.content) {
      if (node.type === 'encrypted' && node.id === encryptionId) {
        found = true
        break
      }

      if (node.type === 'text') {
        encryptedNodeOffset += node.text.length
      } else if (node.type === 'encrypted') {
        const nodeLength = node.content.reduce((sum, n) => sum + (n.type === 'text' ? n.text.length : 0), 0)
        encryptedNodeOffset += nodeLength
      }
    }

    if (!found) {
      logger.warn(`Encrypted node not found: ${encryptionId}`)
      return this
    }

    // Create the unwrap operation
    const operation = createUnwrapEncryptedOperation(
      selection.blockId,
      encryptedNodeOffset,
      encryptionId,
      block.content.find((n) => n.type === 'encrypted' && n.id === encryptionId) as any,
    )

    // Keep cursor at the same position
    const newSelection = createSelection(selection.blockId, selection.offset)

    this.manager.executeOperation(operation, newSelection, 'Remove encryption marking')
    return this
  }

  /**
   * Undoes last operation
   */
  public undo(): this {
    this.manager.undo()
    return this
  }

  /**
   * Redoes last undone operation
   */
  public redo(): this {
    this.manager.redo()
    return this
  }

  /**
   * Indents a list item (increases nesting level)
   */
  public indentListItem(itemId: string): this {
    logger.debug('🔄 indentListItem called with itemId:', itemId)
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()

    // Find the list block containing this item
    const listBlock = ast.blocks.find(
      (block) =>
        block.type === 'list' &&
        (block as any).items.some((item: any) => item.id === itemId || this.findItemInChildren(item, itemId)),
    )

    if (!listBlock || listBlock.type !== 'list') {
      logger.warn('🔄 List block not found for item:', itemId)
      return this
    }

    logger.debug(
      '🔄 Found list block:',
      listBlock.id,
      'with items:',
      (listBlock as any).items.map((item: any) => ({ id: item.id, content: item.content })),
    )

    const listItems = (listBlock as any).items
    const itemIndex = listItems.findIndex((item: any) => item.id === itemId)

    if (itemIndex === -1) {
      // Check if item is in nested structure
      const parentResult = this.findItemParent(listItems, itemId)
      if (!parentResult) {
        logger.warn('🔄 List item not found:', itemId)
        return this
      }
      logger.warn('🔄 Item found in nested structure, not implemented yet')
      return this
    }

    // For indentation, we need a previous sibling to become the parent
    if (itemIndex === 0) {
      logger.warn('🔄 Cannot indent first item in list')
      return this
    }

    logger.debug('🔄 Item found at index:', itemIndex, 'will indent under previous sibling')

    const previousSibling = listItems[itemIndex - 1]

    // Create operation to move item under previous sibling
    const operation = createIndentListItemOperation(
      itemId,
      listBlock.id,
      undefined, // No previous parent at root level
      previousSibling.id,
    )

    this.manager.executeOperation(operation, selection, `Indent list item`)
    return this
  }

  /**
   * Unindents a list item (decreases nesting level)
   */
  public unindentListItem(itemId: string): this {
    logger.debug('🔄 unindentListItem called with itemId:', itemId)
    const ast = this.manager.getCurrentAST()
    const selection = this.manager.getCurrentSelection()

    // Find the list block containing this item
    const listBlock = ast.blocks.find(
      (block) =>
        block.type === 'list' &&
        (block as any).items.some((item: any) => item.id === itemId || this.findItemInChildren(item, itemId)),
    )

    if (!listBlock || listBlock.type !== 'list') {
      logger.warn('🔄 List block not found for item:', itemId)
      return this
    }

    // Find the item and its parent
    const parentResult = this.findItemParent((listBlock as any).items, itemId)
    if (!parentResult) {
      logger.warn('🔄 Cannot unindent item at root level')
      return this
    }

    // Create operation to move item to parent's level
    const operation = createUnindentListItemOperation(
      itemId,
      listBlock.id,
      parentResult.parent.id,
      undefined, // Move to root level
    )

    this.manager.executeOperation(operation, selection, `Unindent list item`)
    return this
  }

  /**
   * Sets block direction (Google Docs-like behavior)
   */
  public setBlockDirection(direction: 'ltr' | 'rtl'): this {
    const selection = this.manager.getCurrentSelection()
    const ast = this.manager.getCurrentAST()
    let currentBlock = findBlockById(ast, selection.blockId)
    let targetBlockId = selection.blockId

    // If block not found, it might be a list item - find the parent list
    if (!currentBlock) {
      for (const block of ast.blocks) {
        if (block.type === 'list') {
          // Check if the selection.blockId is one of the list items
          const foundItem = block.items.find((item) => item.id === selection.blockId)
          if (foundItem) {
            currentBlock = block
            targetBlockId = block.id
            break
          }
        }
      }
    }

    if (!currentBlock || currentBlock.type === 'embed') {
      return this
    }

    // Store previous attributes for undo
    const previousAttrs = { ...currentBlock.attrs }

    // Create new attributes with direction
    const newAttrs = {
      ...currentBlock.attrs,
      dir: direction,
    }

    const operation = createSetBlockAttrsOperation(targetBlockId, newAttrs, previousAttrs)

    this.manager.executeTransaction([operation], selection, `Set direction to ${direction}`)

    return this
  }

  /**
   * Toggles block direction between LTR and RTL
   */
  public toggleBlockDirection(): this {
    const selection = this.manager.getCurrentSelection()
    const ast = this.manager.getCurrentAST()
    let currentBlock = findBlockById(ast, selection.blockId)

    // If block not found, it might be a list item - find the parent list
    if (!currentBlock) {
      for (const block of ast.blocks) {
        if (block.type === 'list') {
          // Check if the selection.blockId is one of the list items
          const foundItem = block.items.find((item) => item.id === selection.blockId)
          if (foundItem) {
            currentBlock = block
            break
          }
        }
      }
    }

    if (!currentBlock || currentBlock.type === 'embed') {
      return this
    }

    // Determine current direction (explicit or inherited)
    const currentDir =
      currentBlock.attrs && 'dir' in currentBlock.attrs && currentBlock.attrs.dir
        ? currentBlock.attrs.dir
        : this.getInheritedDirection(currentBlock, ast)
    const newDir = currentDir === 'rtl' ? 'ltr' : 'rtl'

    return this.setBlockDirection(newDir)
  }

  /**
   * Removes explicit direction attribute (lets block inherit direction)
   */
  public clearBlockDirection(): this {
    const selection = this.manager.getCurrentSelection()
    const ast = this.manager.getCurrentAST()
    const currentBlock = findBlockById(ast, selection.blockId)

    if (
      !currentBlock ||
      currentBlock.type === 'embed' ||
      !(currentBlock.attrs && 'dir' in currentBlock.attrs && currentBlock.attrs.dir)
    ) {
      return this
    }

    // Store previous attributes for undo
    const previousAttrs = { ...currentBlock.attrs }

    // Create new attributes without direction
    const newAttrs = { ...currentBlock.attrs }
    delete newAttrs.dir

    const operation = createSetBlockAttrsOperation(
      selection.blockId,
      Object.keys(newAttrs).length > 0 ? newAttrs : undefined,
      previousAttrs,
    )

    this.manager.executeTransaction([operation], selection, 'Clear explicit direction')

    return this
  }

  /**
   * Auto-detects and sets direction based on block content (first strong character)
   */
  public autoDetectBlockDirection(): this {
    const selection = this.manager.getCurrentSelection()
    const ast = this.manager.getCurrentAST()
    const currentBlock = findBlockById(ast, selection.blockId)

    if (!currentBlock || !('content' in currentBlock)) {
      return this
    }

    // Use the same detection logic as HTML renderer
    // Extract text content from the block
    let fullText = ''
    const extractText = (content: any[]): void => {
      for (const node of content) {
        if (node.type === 'text') {
          fullText += node.text
        }
      }
    }

    extractText(currentBlock.content)

    const detectedDir = detectParagraphDirection(fullText)
    if (detectedDir) {
      this.setBlockDirection(detectedDir)
    }

    return this
  }

  /**
   * Executes a custom command
   */
  public executeCommand(command: EditorCommand): this {
    if (command.canExecute(this.manager)) {
      command.execute(this.manager)
    }
    return this
  }

  private isEmbedBlock(blockId: string): boolean {
    return findBlockById(this.manager.getCurrentAST(), blockId)?.type === 'embed'
  }

  /**
   * Helper: Gets inherited direction for a block (Google Docs behavior)
   */
  private getInheritedDirection(block: Block, ast: ContentAST): 'ltr' | 'rtl' {
    const blockIndex = ast.blocks.findIndex((b) => b.id === block.id)

    // Check previous siblings for direction
    for (let i = blockIndex - 1; i >= 0; i--) {
      const prevBlock = ast.blocks[i]
      if (prevBlock.attrs && 'dir' in prevBlock.attrs && prevBlock.attrs.dir) {
        return prevBlock.attrs.dir
      }
    }

    // Fall back to document default
    return ast.meta.dir
  }

  /**
   * Helper: Finds an item recursively in nested children
   */
  private findItemInChildren(item: any, targetId: string): boolean {
    if (item.id === targetId) {
      return true
    }
    if (!item.children || item.children.length === 0) {
      return false
    }

    return item.children.some((child: any) => this.findItemInChildren(child, targetId))
  }

  /**
   * Helper: Finds the parent of an item in a list structure
   */
  private findItemParent(items: any[], targetId: string, parentItem?: any): { parent: any; index: number } | null {
    for (let i = 0; i < items.length; i++) {
      const item = items[i]

      if (item.id === targetId) {
        return parentItem ? { parent: parentItem, index: i } : null
      }

      if (item.children && item.children.length > 0) {
        const result = this.findItemParent(item.children, targetId, item)
        if (result) {
          return result
        }
      }
    }

    return null
  }

  /**
   * Helper: Gets description for a mark
   */
  private getMarkDescription(mark: Mark): string {
    if (typeof mark === 'string') {
      return mark
    }

    if (mark.type === 'font') {
      return `font ${mark.family}`
    }

    if (mark.type === 'color') {
      return `color ${mark.value}`
    }

    return 'formatting'
  }
}

/**
 * Convenience function to create editor commands
 */
export const createEditorCommands = (manager: AstUndoRedoManager): AstEditorCommands => {
  return new AstEditorCommands(manager)
}

/**
 * Built-in commands
 */
export class BoldCommand implements EditorCommand {
  description = 'Toggle bold formatting'

  canExecute(manager: AstUndoRedoManager): boolean {
    const selection = manager.getCurrentSelection()
    return Boolean(selection.length && selection.length > 0)
  }

  execute(manager: AstUndoRedoManager): boolean {
    const commands = new AstEditorCommands(manager)
    commands.toggleMark('bold')
    return true
  }
}

export class ItalicCommand implements EditorCommand {
  description = 'Toggle italic formatting'

  canExecute(manager: AstUndoRedoManager): boolean {
    const selection = manager.getCurrentSelection()
    return Boolean(selection.length && selection.length > 0)
  }

  execute(manager: AstUndoRedoManager): boolean {
    const commands = new AstEditorCommands(manager)
    commands.toggleMark('italic')
    return true
  }
}

export class UnderlineCommand implements EditorCommand {
  description = 'Toggle underline formatting'

  canExecute(manager: AstUndoRedoManager): boolean {
    const selection = manager.getCurrentSelection()
    return Boolean(selection.length && selection.length > 0)
  }

  execute(manager: AstUndoRedoManager): boolean {
    const commands = new AstEditorCommands(manager)
    commands.toggleMark('underline')
    return true
  }
}

export class InsertParagraphCommand implements EditorCommand {
  description = 'Insert new paragraph'

  canExecute(): boolean {
    return true
  }

  execute(manager: AstUndoRedoManager): boolean {
    const commands = new AstEditorCommands(manager)
    commands.insertBlock('paragraph')
    return true
  }
}

export class SplitBlockCommand implements EditorCommand {
  description = 'Split current block'

  canExecute(): boolean {
    return true
  }

  execute(manager: AstUndoRedoManager): boolean {
    const commands = new AstEditorCommands(manager)
    commands.splitBlock()
    return true
  }
}
